import React, { Fragment, Suspense, useRef } from 'react';
import {
  Box,
  List,
} from '@mui/material';

import { MessageFeedbackView } from '@cvt/components/MessageFeedbackView';

import { useInView } from '@shared/hooks/useInView';
import { useInfiniteMajors } from '../hooks/useInfiniteMajors';

interface ViewProps {
  filters?: Majors.GetListParams;
  renderItem: (job: Majors.Major) => React.ReactNode;
  renderSkeleton: () => React.ReactNode;
}

export const InfiniteMajorsList: React.FC<ViewProps> = ({
  filters = {},
  renderItem,
  renderSkeleton,
}) => {
  const { majors, status, fetchNextPage, hasNextPage, isFetching } = useInfiniteMajors(filters);

  const loadNextRef = useRef<HTMLElement>(undefined);
  const isInView = useInView(loadNextRef);

  const _renderSkeleton = React.useCallback(() => {
    if (isFetching) {
      return Array.from({ length: 3 }).map((v, idx) => <Fragment key={idx}>{renderSkeleton()}</Fragment>);
    }
  }, [isFetching, renderSkeleton]);

  React.useEffect(() => {
    if (isInView && !isFetching && hasNextPage) {
      fetchNextPage();
    }
  }, [isInView, isFetching, fetchNextPage, hasNextPage]);

  return (
    <React.Fragment>
      {status === 'error' && (
        <MessageFeedbackView height="100%" />
      )}
      {status === 'success' && majors.length === 0 ? (
        <MessageFeedbackView
          message="No majors"
          height="100%"
        />
      ) : (
        <List disablePadding>
          {majors.map(major => (
            <Suspense key={major.id} fallback={<>{renderSkeleton()}</>}>
              {renderItem(major)}
            </Suspense>
          ))}
        </List>
      )}

      {hasNextPage && <Box height="1px" ref={loadNextRef} /> }
      {hasNextPage && _renderSkeleton()}
    </React.Fragment>
  );
};
